import React, { useEffect, useRef, useState } from "react";
import TextInputComp from "./TextInputComp";
import CommonButton from "../CommonButton";

export type Option = {
    id: string;
    label: string;
    value: string;
    icon?: React.ReactNode;
    image?: string;
};

type DropdownProps = {
    options: Option[];
    value: Option | null;
    onChange: (option: Option) => void;
    iconPosition?: "left" | "right" | "none";
    className?: string;
    dropdownIcon?: React.ReactNode;
    innerClassName?: string;
    disabled?: boolean;
    showLabel?: boolean;
    label?: string;
    attributeType?: "weight" | "color";
    showStar?: boolean
};

const Dropdown: React.FC<DropdownProps> = ({
    options,
    value,
    onChange,
    iconPosition = "left",
    className = "",
    dropdownIcon,
    innerClassName = '',
    disabled,
    showLabel,
    label,
    attributeType = "color",
    showStar
}) => {

    const [open, setOpen] = useState(false);
    const [showCustom, setShowCustom] = useState(false);
    const [customText, setCustomText] = useState("");
    const [weightUnit, setWeightUnit] = useState("gm");
    const dropdownRef = useRef<HTMLDivElement>(null);

    useEffect(() => {

        const handleClickOutside = (event: MouseEvent) => {

            if (
                dropdownRef.current &&
                !dropdownRef.current.contains(event.target as Node)
            ) {
                setOpen(false);
                setShowCustom(false);
            }
        };

        document.addEventListener("mousedown", handleClickOutside);

        return () => {
            document.removeEventListener("mousedown", handleClickOutside);
        };

    }, []);

    const handleAddCustom = () => {

        let finalLabel = "";

        if (attributeType === "weight") {
            finalLabel = `${customText}${weightUnit}`;
        } else {
            finalLabel = customText;
        }

        const customOption = {
            id: Date.now().toString(),
            label: finalLabel,
            value: finalLabel,
        };

        onChange(customOption);

        setShowCustom(false);
        setOpen(false);
        setCustomText("");
    };

    const renderMedia = (opt: Option) => {
        if (opt.icon) return opt.icon;

        if (opt.image) {
            return (
                <img
                    src={opt.image}
                    alt={opt.label}
                    className="w-6 h-5 object-cover"
                />
            );
        }

        return null;
    };

    return (
        <div className="py-2 w-full" ref={dropdownRef}>

            {showLabel && (
                <label className="pb-2 block flex items-center gap-1">
                    <p className="text-md text-mainBrandColor">{label}</p>
                    {showStar && (
                        <p className="text-sm text-red-500">*</p>
                    )}
                </label>
            )}

            <div className={`relative w-full ${className}`}>

                {/* Selected */}
                <div
                    onClick={() => {

                        if (disabled) return;
                        setShowCustom(false);
                        setOpen(!open);
                    }}
                    className={`
                        w-full
                    flex
                    items-center
                    justify-between
                    border
                    rounded-lg
                    px-3
                    py-2
                    transition-all
                    text-sm 
                    border border-gray-400 
                    hover:border-accent 
                    focus:outline-none 
                    focus:border-accent 
                    focus:ring-1 
                    focus:ring-accent 
                    placeholder:text-xs 

                    ${disabled
                            ? "opacity-70 cursor-not-allowed bg-gray-200 text-gray-900"
                            : "bg-white cursor-pointer"
                        }

                    ${innerClassName}
                `}
                >
                    <div className="flex items-center gap-2">

                        {value && iconPosition === "left" && renderMedia(value)}

                        <span
                            className={`text-sm ${showCustom || value?.label
                                    ? "text-logoHeading"
                                    : "text-gray-400"
                                }`}
                        >
                            {showCustom
                                ? "Custom"
                                : value?.label || "Select Option"}
                        </span>

                        {value && iconPosition === "right" && renderMedia(value)}
                    </div>

                    {dropdownIcon}
                </div>

                {/* Options */}
                {open && (
                    <div className="absolute top-full mt-1 w-full bg-white border rounded-md shadow z-50 overflow-hidden">

                        <div className="max-h-40 overflow-y-auto">
                            {options.map((opt) => (
                                <div
                                    key={opt.value}
                                    onClick={() => {

                                        if (opt.value === "custom") {
                                            setCustomText("");
                                            setOpen(false);
                                            setShowCustom(true);
                                            return;
                                        }

                                        onChange(opt);
                                        setOpen(false);
                                    }}
                                    className="px-3 py-2 hover:bg-gray-100 cursor-pointer flex items-center gap-2"
                                >

                                    {iconPosition === "left" && renderMedia(opt)}

                                    <span>{opt.label}</span>

                                    {iconPosition === "right" && renderMedia(opt)}
                                </div>
                            ))}
                        </div>
                    </div>
                )}
            </div>
            {/* CUSTOM SECTION BELOW DROPDOWN */}
            {showCustom && (

                <div className="mt-2">

                    {/* WEIGHT */}
                    {attributeType === "weight" && (

                        <div className="flex gap-3 items-center">

                            <div className="flex-1">
                                <TextInputComp
                                    type="number"
                                    placeholder="Enter Weight"
                                    value={customText}
                                    onChangeText={setCustomText}
                                />
                            </div>

                            <select
                                value={weightUnit}
                                onChange={(e) =>
                                    setWeightUnit(e.target.value)
                                }
                                className="
                h-[40px]
                min-w-[90px]
                border
                border-gray-400
                rounded-lg
                px-3
                outline-none
                bg-white
            "
                            >
                                <option value="gm">gm</option>
                                <option value="kg">kg</option>
                            </select>

                        </div>
                    )}

                    {/* COLOR */}
                    {attributeType === "color" && (

                        <TextInputComp
                            placeholder="Enter your custom"
                            value={customText}
                            onChangeText={setCustomText}
                        />
                    )}

                    <div className="py-2 flex justify-center items-center">
                        <CommonButton
                            text="Add"
                            variant="filled"
                            rounded="md"
                            className="!w-[50%]"
                            onClick={handleAddCustom}
                        />
                    </div>
                </div>
            )}
        </div>
    );
};

export default Dropdown;